<template>
  <!-- Reaction pills under a message bubble. Each pill is a button:
       click toggles the viewer's own reaction (add if not mine,
       remove if mine). Hover shows the count + emoji shortcode in a
       tooltip so unknown shortcodes still convey meaning. -->
  <div v-if="reactions?.length" class="flex flex-wrap gap-1 mt-1.5" :class="isOwn ? 'justify-end' : ''">
    <button
      v-for="r in reactions"
      :key="r.emoji"
      type="button"
      :title="r.emoji.length > 4 ? `:${r.emoji}: · ${r.count}` : `${r.emoji} · ${r.count}`"
      class="group/reaction inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-semibold transition-colors border"
      :class="r.mine
        ? 'bg-primary/[0.12] text-primary border-primary/30 hover:bg-primary/[0.18]'
        : 'bg-white/[0.04] text-text-muted border-white/[0.06] hover:bg-white/[0.08] hover:text-white'"
      :disabled="busy.has(r.emoji)"
      @click="toggle(r)"
    >
      <!-- Custom emoji (shortcode) → tenant catalog image -->
      <img
        v-if="!isUnicode(r.emoji) && customUrl(r.emoji)"
        :src="customUrl(r.emoji) || ''"
        :alt="`:${r.emoji}:`"
        class="inline-block w-[14px] h-[14px] object-contain"
        loading="lazy"
        draggable="false"
      />
      <!-- Unicode emoji → Twemoji SVG (with native fallback if CDN fails) -->
      <img
        v-else-if="isUnicode(r.emoji) && !brokenTwemoji.has(r.emoji)"
        :src="twemojiUrlFor(r.emoji)"
        :alt="r.emoji"
        class="inline-block w-[13px] h-[13px] object-contain"
        loading="lazy"
        draggable="false"
        @error="brokenTwemoji.add(r.emoji)"
      />
      <span v-else class="leading-none">{{ r.emoji }}</span>
      <span class="tabular-nums">{{ r.count }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue'
import { useCustomEmoji } from '~/composables/chat/useCustomEmoji'
import { splitUnicodeEmoji } from '~/utils/twemoji'

type Reaction = { emoji: string; count: number; mine: boolean }

const props = defineProps<{
  messageId: string
  reactions: Reaction[]
  isOwn?: boolean
}>()

const emit = defineEmits<{
  toggled: [{ emoji: string; nowMine: boolean }]
}>()

const { resolveEmoji } = useCustomEmoji()
const busy = ref<Set<string>>(new Set())
const brokenTwemoji = reactive(new Set<string>())

// Custom shortcodes are stored as `[a-z0-9_-]{1,48}` (no leading colon
// in the persisted reactions array). Unicode emoji are stored as the
// literal char (1+ codepoints). The discriminator: anything pure-ASCII
// matching the shortcode regex is custom; anything else is Unicode.
function isUnicode(s: string): boolean {
  return !/^[a-z0-9_-]{1,48}$/i.test(s)
}

function customUrl(name: string): string | null {
  const entry = resolveEmoji(name)
  return entry?.url || null
}

function twemojiUrlFor(char: string): string {
  const segs = splitUnicodeEmoji(char)
  const first = segs.find(s => s.type === 'unicode_emoji') as any
  return first?.url || ''
}

async function toggle(r: Reaction) {
  if (busy.value.has(r.emoji)) return
  busy.value.add(r.emoji)
  try {
    if (r.mine) {
      await $fetch(`/api/chat/messages/${props.messageId}/reactions`, {
        method: 'DELETE',
        query: { emoji: r.emoji }
      })
      emit('toggled', { emoji: r.emoji, nowMine: false })
    } else {
      await $fetch(`/api/chat/messages/${props.messageId}/reactions`, {
        method: 'POST',
        body: { emoji: r.emoji }
      })
      emit('toggled', { emoji: r.emoji, nowMine: true })
    }
  } catch {
    // WS frame is the source of truth; if the click loses the race
    // (already-removed-by-another-tab) the next frame resyncs.
  } finally {
    busy.value.delete(r.emoji)
  }
}
</script>
